import { isMeetingSessionRoleGreaterThanOrEqual, isSpectator, MeetingOrderedSession, useOrderedMeetingSessions } from "@kalyzee/kast-web-sdk";
import { ChatRef, CyclingQRCodeData, QRCodeData, QRCodeHooks, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { ChatroomStatus, Meeting, MeetingRoom, MeetingRoomStatus, MeetingRoomType, MeetingSession, MeetingSessionRole, MeetingSessionStatus, MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageMap, PageSlaveMode } from "../components/navigation/PageContext";
import { useMessagingBridgeManagerMasterContext, useMessagingBridgeManagerSlaveContext } from "../contexts/messageBridgeManager";
import { MessagingBridgeManagerMasterClient } from "../helpers/bridge/master";
import { SessionMode } from "../store/session/slices";
import { useFocusedMeetingAPI, useMeetingSelector } from "./meeting";
import { useSettings } from "./settings";
import { CarouselData } from "../helpers/bridge/common";
import { takeFromArrayAndLoop } from "../helpers/utils";
import { sdkClient } from "../helpers/sdk";
import { getHttpApiBaseUrl } from "../helpers/request";

export const DEFAULT_CAROUSEL_INTERVAL = 60000;
export const DEFAULT_CAROUSEL_DELAY = 5000;

export const GROUP_PLAYER_COLUMNS = 2;
export const GROUP_PLAYER_ROWS = 3;
export const GROUP_PLAYER_NUMBER = GROUP_PLAYER_COLUMNS * GROUP_PLAYER_ROWS;

export interface GroupWithRotatingSessions {
  group: MeetingRoom,
  sessions: MeetingSession[],
  visibleSessions: MeetingSession[],
}

export interface SlaveQRCodeData {
  display: boolean,
  data?: QRCodeData,
}

interface GroupWithRoundIndex {
  group: MeetingRoom,
  roundIndex: number,
  nextRoundIndex?: number,
}

interface CarouseInfo {
  sessions: MeetingOrderedSession[],
  groups: GroupWithRoundIndex[],
  displayNextCarousel?: Date,
  displayNextCarouselTimeout?: NodeJS.Timer,
  screens: {
    index: number, 
    // Session Mode
    capacity: number,
    roundIndex: number, 
    nextRoundIndex?: number, 
    rounds: { enabled: boolean, items: MeetingOrderedSession[] }[],
    // Group Mode
    groupCapacity: number,
    groupRoundIndex: number,
    groupNextRoundIndex?: number,
    groupRounds: { enabled: boolean, items: GroupWithRoundIndex[] }[],
  }[],
}

const roundSplit = <T> (
  items: T[], 
  screens: { capacity: number, rounds: { enabled: boolean, items: T[] }[] }[],
) => {
  let currRoundIndex = 0;
  let currScreenIndex = 0;

  const pushItem = (item: T): boolean => {
    const currScreen = screens[currScreenIndex];
    let currRound = currScreen.rounds.length > currRoundIndex ? currScreen.rounds[currRoundIndex] : undefined;
    if (currRound === undefined) {
      currRound = { enabled: true, items: [] };
      currScreen.rounds.push(currRound);
    } 
    if (currRound.items.length >= currScreen.capacity) {
      if (currScreenIndex + 1 >= screens.length) {
        currScreenIndex = 0;
        currRoundIndex ++;
        return pushItem(item);;
      }
      currScreenIndex++;
      return pushItem(item);
    }
    currRound.items.push(item);
    return true;
  }

  items.forEach((i) => {
    pushItem(i);
  });
};

export const useMeetingMasterMessagingBridge = () => {
  const [settings] = useSettings();
  const bridge = useMessagingBridgeManagerMasterContext();
  const bridgeRef = useRefAutoUpdate(bridge);

  const meetingCtx = useFocusedMeetingAPI();
  const [meeting, meetingRef] = useMeetingSelector(meetingCtx, (state) => state.meeting);
  const [rooms, roomsRef] = useMeetingSelector(meetingCtx, (state) => state.rooms);
  const [session, sessionRef] = useMeetingSelector(meetingCtx, (state) => state.session);
  const [chatAdapter, chatAdapterRef] = useMeetingSelector(meetingCtx, (state) => {
    const mainChatroom = state.meeting?.chatrooms?.[0];
    if (!mainChatroom) return undefined;
    return state.chatAdapters[mainChatroom.id];
  });

  const orderedSessions = useOrderedMeetingSessions(meeting, useCallback((_, s) => {
    if (!session) return false;
    if (isMeetingSessionRoleGreaterThanOrEqual(s.role, MeetingSessionRole.ADMINISTRATOR)) return false;
    if (isSpectator(s.role)) return false;
    if (s.status !== MeetingSessionStatus.RUNNING) return false;
    return s.id !== session?.id;
  }, [session]));
  const orderedSessionsRef = useRefAutoUpdate(orderedSessions);

  const carouselIntervalRef = useRef<NodeJS.Timer>();
  const carouselInfoRef = useRef<CarouseInfo>();
  const groups = useMemo(() => rooms?.filter(
    r => r.type === MeetingRoomType.Group && r.status !== MeetingRoomStatus.Closed) ?? [], 
    [rooms],
  );
  const groupsRef = useRefAutoUpdate(groups);

  const hasQRCodeClientsFn = () => {
    const clients = bridgeRef.current?.getActiveClients<SessionMode.SLAVE, PageMap[SessionMode.SLAVE]>(true);
    return clients && clients.some(c => c.data?.data.screen === 1);
  }
  const [hasQRCodeClients, setHasQRCodeClients] = useState(() => hasQRCodeClientsFn());
  useEffect(() => {
    const handle = bridgeRef.current?.addEventListener("clientChange", () => {
      setHasQRCodeClients(hasQRCodeClientsFn());
    });
    return () => handle?.flush();
  }, []);

  const getQRCodeData = useCallback(async (): Promise<QRCodeData | undefined> => {
    const wsClient = sdkClient.getWebsocketClient();
    if (!wsClient) return undefined;
    const meetingId = meeting?.id;
    if (!meetingId) return undefined;
    const result = await wsClient.emitter.meeting.generateUserDataOnsiteTicket({ meetingId });
    if (!result.response || result.error) return undefined;
    const { ticket, ticketExpiresAfter, ticketExpiresAt } = result.response;
    // const url = `${getHttpApiBaseUrl()}/meeting/ticket/${ticket}`;
    return {
      value: ticket,
      expiration: ticketExpiresAfter && ticketExpiresAt ? {
        after: ticketExpiresAfter,
        at: new Date(ticketExpiresAt),
      } : undefined,
    }
  }, [meeting?.id]);
  const qrCodeData = QRCodeHooks.useCycling(
    meeting?.callOngoing && hasQRCodeClients ? getQRCodeData : undefined,
    5000,
  );
  
  const generateGroupWithRotatingSessions = (
    group: MeetingRoom,
    roundIndex: number,
  ): GroupWithRotatingSessions => {
    const sessions: MeetingOrderedSession[] = [];
    const normalSessions: MeetingOrderedSession[] = [];
    const leaderSessions: MeetingOrderedSession[] = [];

    orderedSessionsRef.current.forEach(session => {
      if (group.enlistedUserIds?.includes(session.userId)) {
        sessions.push(session);
        if (group.leaderUserId === session.userId) {
          leaderSessions.push(session);
        } else {
          normalSessions.push(session);
        }
      }
    });

    // 1 - Areas :
    let leaderAreaSize = 0;
    if (leaderSessions.length) {
      // If there is leader sessions, they will be locked in the first positions,
      // however if there is not enough space for everyone, the normal sessions have 
      // a priority. There will always be atleast 1 locked session slot.
      leaderAreaSize = Math.min(leaderSessions.length, Math.max(1, GROUP_PLAYER_NUMBER - normalSessions.length));
    }
    const normalAreaSize = Math.min(normalSessions.length, GROUP_PLAYER_NUMBER - leaderAreaSize);

    // 2 - Visible sessions :

    let visibleLeaderSessions: MeetingOrderedSession[] = [];
    let visibleNormalSessions: MeetingOrderedSession[] = [];

    if (leaderAreaSize) {
      const leaderPageNb = Math.ceil(leaderSessions.length / leaderAreaSize);
      const currentLeaderPage = roundIndex % leaderPageNb;
      visibleLeaderSessions = takeFromArrayAndLoop(leaderSessions, currentLeaderPage * leaderAreaSize, leaderAreaSize);
    }

    if (normalAreaSize) {
      const normalPageNb = Math.ceil(normalSessions.length / normalAreaSize);
      const currentNormalPage = roundIndex % normalPageNb;
      visibleNormalSessions = takeFromArrayAndLoop(normalSessions, currentNormalPage * normalAreaSize, normalAreaSize);
    }
  
    // 3 - combining the 2 visible areas :
    const visibleSessions = visibleLeaderSessions.concat(visibleNormalSessions);

    return {
      group,
      sessions: sessions.map(s => s.session),
      visibleSessions: visibleSessions.map(s => s.session),
    };
  };

  const getMessagingBridgeClients = (): MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>[] => {
    return bridgeRef.current?.getActiveClients(true).filter((c) => c.data?.mode === SessionMode.SLAVE && c.data?.page === PageSlaveMode.MEETING) ?? [];
  };

const generateGroupsWithRoundIndex = (old?: GroupWithRoundIndex[]): GroupWithRoundIndex[] => {
  const output: GroupWithRoundIndex[] = [];
  groupsRef.current.forEach(group => {
    const oldGroup = old?.find(g => g.group.id === group.id);
    output.push({ group, roundIndex: oldGroup?.roundIndex ?? -1 });
  });
  return output;
};  

  const generateCarouselInfo = () => {
    const sessions = orderedSessionsRef.current;
    const previousCarouselInfo = carouselInfoRef.current;
    const newCarouselInfo: CarouseInfo = { 
      sessions,
      groups: generateGroupsWithRoundIndex(carouselInfoRef.current?.groups),
      displayNextCarousel: previousCarouselInfo?.displayNextCarousel,
      displayNextCarouselTimeout: previousCarouselInfo?.displayNextCarouselTimeout,
      screens: []
    };
    carouselInfoRef.current = newCarouselInfo;

    // Screens
    // ---->
    const clients = getMessagingBridgeClients();
    const groupedByScreen: { [key: number]: MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>[] } = {};
    clients.forEach((c) => {
      const screen = c.data?.data?.screen;
      if (screen !== undefined) {
        if (!groupedByScreen[screen]) groupedByScreen[screen] = [];
        groupedByScreen[screen].push(c);
      }
    });
    const screens: number[] = Object.keys(groupedByScreen)
      .map((k) => Number(k))
      .filter((n) => !isNaN(n))
      .sort((a, b) => {
        // priority to screen > 3
        if ((a === 1 || a === 2) && (b === 1 || b === 2)) return a - b;
        if (a === 1 || a === 2) return 1;
        if (b === 1 || b === 2) return -1;
        return a - b;
      });
    screens.forEach((s) => {
      if (s <= 0) return; // not for carousel
      const currClients = groupedByScreen[s];
      let capacity = 0;
      currClients.forEach((c) => {
        const currCapacity = c.data?.data?.players;
        if (currCapacity) capacity = Math.max(capacity, currCapacity);
      });
      let groupCapacity = 0;
      currClients.forEach((c) => {
        const currCapacity = c.data?.data?.groups;
        if (currCapacity) groupCapacity = Math.max(groupCapacity, currCapacity);
      });

      const previousScreen = previousCarouselInfo?.screens?.find((screen) => screen.index === s);

      // Session Mode
      const roundIndex = previousScreen?.roundIndex ?? 0;
      const nextRoundIndex = previousScreen?.nextRoundIndex;
      // Group Mode
      const groupRoundIndex = previousScreen?.groupRoundIndex ?? 0;
      const groupNextRoundIndex = previousScreen?.groupNextRoundIndex;
      
      newCarouselInfo.screens.push({
        index: s,
        capacity,
        groupCapacity,
        
        roundIndex,
        nextRoundIndex,
        rounds: [],
        
        groupRoundIndex,
        groupNextRoundIndex,
        groupRounds: [],
      })
    });
    // <----
    
    if (newCarouselInfo.screens.length < 1) return;
    // Sessions
    roundSplit(sessions, newCarouselInfo.screens.map(
      s => ({ 
        capacity: s.capacity, 
        rounds: s.rounds,
      })
    ));

    // Groups
    roundSplit(
      carouselInfoRef.current.groups, 
      newCarouselInfo.screens.map(
        s => ({ 
          capacity: s.groupCapacity, 
          rounds: s.groupRounds,
        })
      )
    );
  
    // remove rounds if only disconnect user & round > 1
    newCarouselInfo.screens?.forEach((s) => {
      s.rounds.forEach((round) => {
        let containsOnlyDisconnectedUsers = true;
        for (let session of round.items) {
          if (session.connected) {
            containsOnlyDisconnectedUsers = false;
            break;
          }
        }
        if (containsOnlyDisconnectedUsers) {
          round.enabled = false;
        }
      });

      s.roundIndex = getValidRoundIndex(s, s.roundIndex);
      if (s.nextRoundIndex) s.nextRoundIndex = getValidRoundIndex(s, s.nextRoundIndex);
    });
  };

  const getClientMappedByScreen = (): Map<number, MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>[]>  => {
    const clients = getMessagingBridgeClients();
    const map = new Map<number, MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>[]>();
    clients.forEach((c) => {
      const screen = c.data?.data?.screen;
      if (screen !== undefined) {
        let curr = map.get(screen);
        if (!curr) curr = [];
        curr.push(c);
        map.set(screen, curr);
      }
    });
    return map;
  }

  // get next index of round enabled
  // if all the screens are disabled, we return the first index and it should be displayed
  const getValidRoundIndex = (screen: CarouseInfo['screens'][number], startedAt: number) => {
    if (!screen.rounds.length) return 0;
    let currIndex = startedAt % screen.rounds.length;
    const firstIndex = currIndex;
    let isValid = false;
    do {
      const round = screen.rounds[currIndex];
      if (round.enabled) {
        isValid = true;
        break;
      }
      currIndex = (currIndex + 1) % screen.rounds.length
    } while (currIndex !== firstIndex);
    return isValid ? currIndex : 0;
  }

  const getValidGroupRoundIndex = (screen: CarouseInfo['screens'][number], startedAt: number) => {  
    if (!screen.groupRounds.length) return 0;
    return startedAt % screen.groupRounds.length;
  };

  const convertToPageInfo = (index: number, rounds: CarouseInfo['screens'][number]['rounds']): {page: number, pageCount: number} => {
    let page = -1;
    let pageCount = 0;
    rounds.forEach((r, i) => {
      if (r.enabled) {
        if (i<= index) page ++;
        pageCount ++;
      }
    })
    // if everything is disabled, we pick the first page
    return { page: Math.max(0, page), pageCount };
  };

  const updateCurrentCarousel = useCallback(debounce(() => {
    generateCarouselInfo();
    const carouselInfo = carouselInfoRef.current;
    if (!carouselInfo) return;

    const clients = getClientMappedByScreen();

    carouselInfo.screens.forEach((screen) => {
      const currClients = clients.get(screen.index);

      if (groupsRef.current.length) {
        // Group Mode
        if (!screen.groupRounds.length) {
          currClients?.forEach((client) => {
            bridgeRef.current?.postDisplayCarousel({ mode: "groups", groups: [] }, 0, 0, 0, client.id);
          });
          return;
        }
        // Setting current group round
        screen.groupRoundIndex = getValidGroupRoundIndex(screen, screen.groupRoundIndex);
      } else {
        // Session Mode
        if (!screen.rounds.length) {
          currClients?.forEach((client) => {
            bridgeRef.current?.postDisplayCarousel({ mode: "sessions", sessions: [] }, 0, 0, 0, client.id);
          });
          return;
        }
        // Setting current round
        screen.roundIndex = getValidRoundIndex(screen, screen.roundIndex);
      }

      currClients?.forEach((c) => {
        if (groups.length) {
          // Group Mode
          const currCapacity = Math.min(screen.groupCapacity, c.data?.data?.groups ?? 0);
          const round = screen.groupRounds[screen.groupRoundIndex]; // Using current group round
          const data: CarouselData = {
            mode: "groups",
            groups: round ? round.items.slice(0, currCapacity).map(
              group => generateGroupWithRotatingSessions(group.group, group.roundIndex)
            ) : [],
          };
          bridgeRef.current?.postDisplayCarousel(data, 0, screen.groupRoundIndex ?? 0, screen.groupRounds.length, c.id);
        } else {
          // Session Mode
          const currCapacity = Math.min(screen.capacity, c.data?.data?.players ?? 0);
          const pageInfo = convertToPageInfo(screen.roundIndex, screen.rounds);
          const round = screen.rounds[pageInfo.page]; 
          const data: CarouselData = {
            mode: "sessions",
            sessions: round.items.slice(0, currCapacity).map((s) => s.session),
          };
          bridgeRef.current?.postDisplayCarousel(data, 0, pageInfo.page, pageInfo.pageCount, c.id);
        }
      });
    });

    /**
     * 
     * Bug explaination when receiving a lot of fast updates :
     * - setNextCarousel was called from the main setInterval with page=N+1 (next page)
     * - then right away, here postDisplayCarousel was called with delay=0, forcing slaves to update back to page=N
     *   -> roundIndex still pointing to page N, because the roundIndex=N+1 is done at the end of the setNextCarousel timeout
     * - then here setNextCarousel was not called again because of a condition that delay must be > 0
     * 
     * Fix :
     * - here, setNextCarousel is now called again, with delay=0, immediatly setting the next page again
     */

    // refresh next carousel if already scheduled
    if (carouselInfo.displayNextCarousel) {
      const now = new Date();
      const delay = carouselInfo.displayNextCarousel.getTime() - now.getTime();
      setNextCarousel("increment", Math.max(0, delay), false);
    }
  }, 500, { leading: true, maxWait: 500 }), [bridge, groups]);

  const setNextCarousel = (
    mode: 'increment' | 'restart', 
    delay: number = 0,
    rotateGroups = true,
  ) => {
    const carouselInfo = carouselInfoRef.current;
    if (!carouselInfo) return;

    const clients = getClientMappedByScreen();

    carouselInfo.screens.forEach((screen) => {
      const currClients = clients.get(screen.index);
      if (groupsRef.current.length) {
        // Group Mode
        if (!screen.groupRounds.length) {
          currClients?.forEach((c) => {
            bridgeRef.current?.postDisplayCarousel({ mode: "groups", groups: [] }, delay, 0, 0, c.id);
          });
          return;
        }
        screen.groupNextRoundIndex = getValidGroupRoundIndex(screen, screen.groupRoundIndex + 1);
      } else {
        // Session Mode
        if (!screen.rounds.length) {
          currClients?.forEach((c) => {
            bridgeRef.current?.postDisplayCarousel({ mode: "sessions", sessions: [] }, delay, 0, 0, c.id);
          });
          return;
        }
        let nextRoundIndex = getValidRoundIndex(screen, screen.roundIndex);
        if (mode === "restart") nextRoundIndex =  getValidRoundIndex(screen, 0);
        else if (mode === 'increment') nextRoundIndex = getValidRoundIndex(screen, screen.roundIndex + 1);
        // Setting next round
        screen.nextRoundIndex = nextRoundIndex;
      }

      currClients?.forEach((c) => {
        if (groupsRef.current.length) {
          // Group Mode
          const currCapacity = Math.min(screen.groupCapacity, c.data?.data?.groups ?? 0);
          const round = screen.groupRounds[screen.groupNextRoundIndex ?? 0]; // Using next group round
          const data: CarouselData = {
            mode: "groups",
            groups: [],
          };
          round.items.slice(0, currCapacity).forEach((group) => {
            if (rotateGroups) {
              let nextGroupRoundIndex: number;
              if (mode === "restart") nextGroupRoundIndex = 0;
              else nextGroupRoundIndex = group.roundIndex + 1;
              group.nextRoundIndex = nextGroupRoundIndex;
            }
            data.groups.push(generateGroupWithRotatingSessions(group.group, group.nextRoundIndex ?? 0));
          });
          bridgeRef.current?.postDisplayCarousel(data, delay, screen.groupNextRoundIndex ?? 0, screen.groupRounds.length, c.id);
        } else {
          // Session Mode
          const currCapacity = Math.min(screen.capacity, c.data?.data?.players ?? 0);
          const pageInfo = convertToPageInfo(screen.nextRoundIndex ?? 0, screen.rounds);
          const round = screen.rounds[pageInfo.page]; // Using adjusted round
          const data: CarouselData = {
            mode: "sessions",
            sessions: round.items.slice(0, currCapacity).map((s) => s.session),
          };  
          bridgeRef.current?.postDisplayCarousel(data, delay, pageInfo.page, pageInfo.pageCount, c.id);
        }
      })
    });

    if (carouselInfo.displayNextCarouselTimeout) clearTimeout(carouselInfo.displayNextCarouselTimeout);

    // saved date of next carousel will be displayed
    const updateIndexes = () => {
      // update local indexes
      carouselInfo.displayNextCarousel = undefined;
      carouselInfo.screens.forEach((s) => {
        // next carousel displayed
        if(s.nextRoundIndex !== undefined) s.roundIndex = s.nextRoundIndex;
        if (s.groupNextRoundIndex !== undefined) s.groupRoundIndex = s.groupNextRoundIndex;
        s.nextRoundIndex = undefined;
        s.groupNextRoundIndex = undefined;
      });
    };
    
    if (delay > 0) {
      carouselInfo.displayNextCarousel = new Date(new Date().getTime() + delay);
      // update indexes after delay
      carouselInfo.displayNextCarouselTimeout = setTimeout(updateIndexes, delay);
    } else {
      // update indexes now
      updateIndexes();
      carouselInfo.displayNextCarousel = undefined;
      carouselInfo.displayNextCarouselTimeout = undefined;
    }
  }

  const stopCarouselSystem = () => {
    if (carouselIntervalRef.current) clearInterval(carouselIntervalRef.current);
    if (carouselInfoRef.current?.displayNextCarouselTimeout) clearTimeout(carouselInfoRef.current.displayNextCarouselTimeout);
    carouselIntervalRef.current = undefined;
    carouselInfoRef.current = undefined;
  };

  const startCarouselSystem = () => {
    stopCarouselSystem();
    generateCarouselInfo();
    setNextCarousel('restart', 0);
    carouselIntervalRef.current = setInterval(() => {
      setNextCarousel('increment', settings?.carousel?.prelaodDelay ?? DEFAULT_CAROUSEL_DELAY);
    }, settings?.carousel?.interval ?? DEFAULT_CAROUSEL_INTERVAL);
  };

  const carouselSystemIsProcessing = () => {
    return carouselIntervalRef.current !== undefined;
  };

  const broadcastMeeting = useCallback(
    debounce(
      () => {
        if (!bridge) return;
        const meeting = meetingRef.current;
        const rooms = roomsRef.current;
        const session = sessionRef.current;
        if (meeting && meeting?.state !== MeetingState.STOPPED) {
          bridge.postMeeting(meeting, rooms, session);
        } else {
          bridge.postMeeting(undefined, undefined);
        }
      },
      100,
      { maxWait: 300 }
    ),
    [bridge]
  );

  const broadcastChat = useCallback(
    debounce(
      () => {
        if (!bridge) return;
        const adapter = chatAdapterRef.current;
        if (!adapter) return;
        const chatroom = meetingRef.current?.chatrooms?.find((c) => c.id === adapter.getChatroomId());
        if (!chatroom) return;
        bridge.postChat(chatroom.id, adapter.getChatroomState() ?? chatroom.status, adapter.getMessages());
      },
      100,
      { maxWait: 300 }
    ),
    [bridge]
  );

  useEffect(() => {
    if (!bridge) return;
    const getMeetingListener = bridge.addEventListener("requestMeeting", (_, respond) => {
      respond(meetingRef.current, roomsRef.current, sessionRef.current);
    });
    const getChatListener = bridge.addEventListener("requestChat", (_, respond) => {
      const adapter = chatAdapterRef.current;
      if (!adapter) return;
      const chatroom = meetingRef.current?.chatrooms?.find((c) => c.id === adapter.getChatroomId());
      if (!chatroom) return;
      respond(chatroom.id, adapter.getChatroomState() ?? chatroom.status, adapter.getMessages());
    });

    const clientDataListener = bridge.addEventListener("clientChange", () => {
      const newClients = getMessagingBridgeClients();
      if (newClients.length) {
        if (!carouselSystemIsProcessing()) startCarouselSystem();
        else updateCurrentCarousel();
      } else if (!newClients.length) {
        stopCarouselSystem();
      }
    });

    return () => {
      getMeetingListener.flush();
      getChatListener.flush();
      clientDataListener.flush();
    };
  }, [bridge, updateCurrentCarousel]);

  useEffect(() => {
    broadcastMeeting();
  }, [meeting, session])

  useEffect(() => {
    if (!meetingCtx) {
      stopCarouselSystem();
      return;
    }
    if (!meeting) {
      stopCarouselSystem();
    } else {
      const clients = getMessagingBridgeClients();
      if (!carouselSystemIsProcessing() && clients.length) {
        startCarouselSystem();
      } else if (carouselSystemIsProcessing()) {
        updateCurrentCarousel();
      }
    }
    broadcastMeeting();
  }, [meetingCtx, broadcastMeeting, updateCurrentCarousel]);

  useEffect(() => {
    const chatCleanMessagesListener = chatAdapter?.addEventListener("cleanMessages", () => {
      broadcastChat();
    });
    const chatMessagesListener = chatAdapter?.addEventListener("messages", () => broadcastChat());
    const chatNewMessagesListener = chatAdapter?.addEventListener("newMessages", () => broadcastChat());
    const chatUpdateMessageListener = chatAdapter?.addEventListener("updateMessage", () => broadcastChat());
    const chatUpdateManyMessagesListener = chatAdapter?.addEventListener("updateMessages", () => broadcastChat());
    const chatStateListener = chatAdapter?.addEventListener("state", () => broadcastChat());
    return () => {
      chatMessagesListener?.flush();
      chatCleanMessagesListener?.flush();
      chatNewMessagesListener?.flush();
      chatUpdateMessageListener?.flush();
      chatUpdateManyMessagesListener?.flush();
      chatStateListener?.flush();
    };
  }, [chatAdapter, broadcastChat]);

  useEffect(() => {
    if (!bridge) return;
    bridge.postQRCodeData(qrCodeData);
  }, [bridge, qrCodeData]);

  useEffect(() => {
    return () => {
      bridgeRef.current?.postMeeting(undefined, undefined);
      stopCarouselSystem();
    };
  }, []);

  return bridge;
};

export const useMeetingSlaveMessagingBridge = (chatRef?: React.RefObject<ChatRef>) => {
  const bridge = useMessagingBridgeManagerSlaveContext();
  const bridgeRef = useRefAutoUpdate(bridge);
  const [meeting, setMeeting] = useState<Meeting>();
  const [rooms, setRooms] = useState<MeetingRoom[]>();
  const [session, setSession] = useState<MeetingSession>();
  const [qrCodeData, setQRCodeData] = useState<CyclingQRCodeData>();
  const isInitiatedRef = useRef(false);
  const currentChatroomId = useRef<string>();

  useEffect(() => {
    if (!bridge) return;
    const readyListener = bridge.addEventListener("ready", () => {
      bridge.postRequestMeeting();
    });
    const meetingListener = bridge.addEventListener("meeting", (msg) => {
      const { meeting, rooms, session } = msg.content;
      setMeeting(meeting);
      setRooms(rooms);
      setSession(session);
    });
    const chatListener = bridge.addEventListener("chat", (msg) => {
      const { state, messages } = msg.content;
      if (state === ChatroomStatus.disabled) chatRef?.current?.disable();
      else chatRef?.current?.enable();
      chatRef?.current?.setMessages(
        messages.map((m) => {
          m.date = new Date(m.date); // we need to convert date to Date (date change to "string" type by passing by the websocket)
          return m;
        })
      );
    });
    const qrCodeListener = bridge.addEventListener("qrCodeData", (msg) => {
      const { data } = msg.content;
      if (data?.data?.expiration?.at) {
        // we need to convert date to Date (date change to "string" type by passing by the websocket)
        data.data.expiration.at = new Date(data.data.expiration.at);
      }
      setQRCodeData(data);
    });

    const disconnectListener = bridge.addEventListener("disconnect", () => {
      setMeeting(undefined);
      setRooms(undefined);
      setSession(undefined);
      setQRCodeData(undefined);
      currentChatroomId.current = undefined;
      bridge.postRequestMeeting();
    });

    if (!isInitiatedRef.current) {
      isInitiatedRef.current = true;
      bridge.postRequestMeeting();
    }

    return () => {
      readyListener.flush();
      meetingListener.flush();
      chatListener?.flush();
      disconnectListener.flush();
      qrCodeListener.flush();
    };
  }, [bridge]);

  useEffect(() => {
    if (!meeting) return;
    if (meeting.chatrooms?.length) {
      const chatroomId = meeting.chatrooms[0].id;
      if (chatroomId !== currentChatroomId.current) {
        currentChatroomId.current = chatroomId;
        bridgeRef.current?.postRequestChat(chatroomId);
      }
    }
  }, [meeting]);

  return { bridge, meeting, rooms, session, qrCodeData };
};
